import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { Box, CircularProgress, Typography } from '@mui/material';

interface PrivateRouteProps {
  children: ReactNode;
  requiredPermissions?: string[];
  requiredRoles?: string[];
}

/**
 * Private Route Component
 * Protects routes requiring authentication and optionally specific permissions/roles
 */
const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  requiredPermissions = [],
  requiredRoles = [],
}) => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const {
    hasPermission,
    hasAllPermissions,
    isLoading: permissionLoading,
  } = usePermissions();

  // Show loading while checking authentication
  if (authLoading || permissionLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check permissions if required
  // BYPASS: SUPER_ADMIN and ADMIN have full access, ignore permission checks
  const roleCode = user?.role?.code;
  const isPrivileged = roleCode === 'SUPER_ADMIN' || roleCode === 'ADMIN';

  if (requiredPermissions.length > 0 && !isPrivileged) {
    if (!hasAllPermissions(requiredPermissions)) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <Typography variant="h5" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="textSecondary">
            You do not have permission to access this page.
          </Typography>
        </Box>
      );
    }
  }

  // Check roles if required
  if (requiredRoles.length > 0) {
    if (!user) return null;
    const roleHierarchy: Record<string, number> = {
      SUPER_ADMIN: 1,
      ADMIN: 2,
      FACULTY: 3,
      TEACHER: 3,
      STAFF: 3,
      STUDENT: 4,
    };

    const userRoleHierarchy = roleHierarchy[user.role.code] || 999;
    const hasRequiredRole = requiredRoles.some((role) => {
      const requiredHierarchy = roleHierarchy[role] || 999;
      return userRoleHierarchy <= requiredHierarchy;
    });

    if (!hasRequiredRole) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <Typography variant="h5" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Your role does not have access to this page.
          </Typography>
        </Box>
      );
    }
  }

  return <>{children}</>;
};

export default PrivateRoute;
